/**
 * src/pages/AdminLogin.tsx
 *
 * File-level:
 * - Admin login page that displays the login card and sidebars.
 * - Uses AdminLoginForm component which calls the backend login API.
 */

import React from 'react';
import { useNavigate } from 'react-router';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import AdminLoginForm from '../components/admin/AdminLoginForm';
import { Shield } from 'lucide-react';

/**
 * AdminLogin
 *
 * @description Page wrapper for admin login. When login succeeds it stores a small
 * local session and navigates to /admin.
 */
export default function AdminLogin(): JSX.Element {
  const navigate = useNavigate();

  /**
   * handleSuccess
   *
   * @description Called by AdminLoginForm when authentication succeeds.
   * Stores a minimal local session and redirect to admin dashboard.
   * @param user Authenticated admin user object
   */
  function handleSuccess(user: { userId: string; displayName?: string }) {
    try {
      localStorage.setItem('adminToken', 'admin-session-local');
      localStorage.setItem('adminUser', JSON.stringify(user));
    } catch {
      // ignore localStorage errors
    }
    navigate('/admin');
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 py-8">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Sidebar (decorative) */}
          <div className="hidden lg:block lg:col-span-2" />

          {/* Main */}
          <div className="lg:col-span-8 flex items-center justify-center min-h-[80vh]">
            <Card className="w-full max-w-md border-2 border-blue-200 shadow-2xl">
              <CardHeader className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-lg p-4">
                <div className="flex items-center gap-3">
                  <Shield className="w-8 h-8" />
                  <div>
                    <CardTitle className="text-2xl">Admin Login</CardTitle>
                    <CardDescription className="text-blue-100">
                      Sign in with your admin credentials
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <AdminLoginForm onSuccess={handleSuccess} />
                <div className="mt-6 text-center">
                  <p className="text-gray-500 text-xs">© 2026 RJBD. Admin access requires authorization.</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Sidebar (decorative) */}
          <div className="hidden lg:block lg:col-span-2" />
        </div>
      </div>
    </div>
  );
 }